import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import DealDrawer from "@/components/DealDrawer";
import CityFilter from "@/components/CityFilter";
import { Input } from "@/components/ui/input";
import { Loader2, RefreshCw, UserRound, Clock, Search, X } from "lucide-react";

export default function Pipeline() {
  const [columns, setColumns] = useState([]);
  const [cities, setCities] = useState([]);
  const [selCities, setSelCities] = useState([]);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [dealId, setDealId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (selCities.length) params.cities = selCities.join(",");
    if (q) params.search = q;
    api.get("/deals/board", { params }).then(({ data }) => { setColumns(data.columns); setCities(data.cities || []); }).finally(() => setLoading(false));
  }, [selCities, q]);
  useEffect(() => { load(); }, [load]);

  const onDrop = async (stage) => {
    if (!dragId) return;
    const id = dragId; setDragId(null);
    try { await api.put(`/deals/${id}/stage`, { stage }); load(); } catch {}
  };

  const openDeal = (id) => { setDealId(id); setDrawerOpen(true); };
  const total = columns.reduce((n, c) => n + c.count, 0);
  const filtered = selCities.length > 0 || q;

  return (
    <div>
      <PageHeader title="Pipeline" subtitle="Drag deals across stages. Every move is logged.">
        <button onClick={load} className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1.5" data-testid="pipeline-refresh"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </PageHeader>

      <div className="px-8 pt-5 flex items-center gap-2 flex-wrap" data-testid="pipeline-filters">
        <CityFilter options={cities} value={selCities} onChange={setSelCities} />
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Search property, area, rep…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" data-testid="pipeline-search" />
        </div>
        {filtered && (
          <button onClick={() => { setSelCities([]); setSearch(""); }} className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1" data-testid="pipeline-clear-filters"><X className="h-3.5 w-3.5" /> Clear</button>
        )}
        <span className="text-xs text-gray-400 ml-auto" data-testid="pipeline-total">{total} open deal(s){filtered ? " matching" : ""}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0047FF]" /></div>
      ) : (
        <div className="px-8 py-5 overflow-x-auto" data-testid="pipeline-board">
          <div className="flex gap-4 min-w-max pb-4">
            {columns.map((col) => (
              <div key={col.stage} className="w-80 shrink-0 bg-gray-100 rounded-lg flex flex-col"
                onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(col.stage)} data-testid={`column-${col.stage}`}>
                <div className="px-3 py-2.5 flex items-center justify-between border-b border-gray-200">
                  <span className="font-heading font-bold text-sm text-gray-800">{col.label}</span>
                  <span className="text-xs text-gray-500">{col.count} · {col.bed_rollup} beds</span>
                </div>
                <div className="p-2 space-y-2 min-h-[120px]">
                  {col.deals.map((d) => (
                    <div key={d.id} draggable onDragStart={() => setDragId(d.id)} onClick={() => openDeal(d.id)}
                      className="bg-white border border-gray-200 rounded-md p-3 cursor-pointer hover:-translate-y-[1px] hover:shadow-sm transition-all" data-testid={`deal-card-${d.id}`}>
                      <div className="font-semibold text-sm text-gray-900 truncate">{d.property_name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{d.property_city || "—"}</div>
                      <div className="flex items-center justify-between mt-2 text-[11px] text-gray-500">
                        <span className="flex items-center gap-1"><UserRound className="h-3 w-3" />{d.rep_name || "—"}</span>
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{d.days_in_stage}d</span>
                      </div>
                      {d.consultant_name && <div className="text-[11px] text-gray-400 mt-1 truncate">via {d.consultant_name}</div>}
                      {d.shared_property && (
                        <div className="mt-2 text-[10px] bg-amber-50 border border-amber-200 text-amber-800 rounded px-1.5 py-0.5" data-testid="card-shared-badge">
                          Shared · {d.shared_property.rep_name} @ {d.shared_property.stage}
                        </div>
                      )}
                    </div>
                  ))}
                  {col.deals.length === 0 && <div className="text-xs text-gray-400 text-center py-6">Drop here</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <DealDrawer dealId={dealId} open={drawerOpen} onOpenChange={setDrawerOpen} onChanged={load} />
    </div>
  );
}
